import { ReactNode, useContext } from "react";
import styled, { useTheme } from "styled-components";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LinkButton from "common/Buttons/LinkButton";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import TextWithIcon from "common/Text/TextWithIcon";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import PublicBotCardMainInfo from "features/bots/BotForm/General/PublicBotCardMainInfo";
import AmountSelector from "features/bots/BotForm/General/AmountSelector";
import AccountsList from "features/onboarding/overview/accountsList/AccountsList";
import { OnboardingStyles } from "features/onboarding/common/styles";
import {
  ONBOARDING_BOT_CONFIGURATION_ID,
  ONBOARDING_CONFIRM_PANEL_ID,
  ONBOARDING_CUSTOMIZE_ID,
} from "testIds";
import Button from "common/Buttons/Button";
import PhoneActionBar from "features/onboarding/common/PhoneActionBar";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";

interface OnboardingConfirmPanelProps {
  /** The form's own create button (direct demo creation). */
  createButton: ReactNode;
  onCustomize?: () => void;
  onShowConfiguration?: () => void;
}

const text = (value: unknown): string | undefined =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : undefined;

/**
 * Confirm mode of the onboarding's bot setup, laid out like a preset's
 * "Review before you start" card: the bot, the symbol, the account with its
 * balance, the method's key numbers and the amount per trade, then one
 * button to start it, with the full form one tap away. Rendered from inside
 * the (hidden) form, so its defaults and its creation path are the form's.
 */
const OnboardingConfirmPanel = ({
  createButton,
  onCustomize,
  onShowConfiguration,
}: OnboardingConfirmPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { formValues, botType, isLoading, gridsData } =
    useContext(BotFormContext)!;
  const symbol = Array.isArray(formValues.symbols)
    ? formValues.symbols[0]
    : undefined;
  const name = text(formValues.name);
  const account =
    typeof formValues.account === "object" &&
    formValues.account &&
    "client_name" in formValues.account
      ? formValues.account
      : undefined;
  // The live grids (formValues is a snapshot that catches up a beat later,
  // so it would first show the form's own default range).
  const grids = gridsData ?? formValues.gridsData;
  const min = text(grids?.priceRange?.min);
  const max = text(grids?.priceRange?.max);
  const gridFacts =
    botType === BotType.GRID && min && max
      ? [
          { label: t("intent.confirm_range"), value: `${min} – ${max}` },
          ...(text(grids?.numGrids)
            ? [
                {
                  label: t("intent.confirm_grids"),
                  value: text(grids?.numGrids)!,
                },
              ]
            : []),
        ]
      : [];
  const presetReady = useContext(OnboardingBotSetupContext)?.presetReady;
  const ready = !isLoading && !!symbol && presetReady !== false;

  return (
    <OnboardingStyles.OverviewCard
      $isMobile={isMobile}
      data-testid={ONBOARDING_CONFIRM_PANEL_ID}
    >
      <LoadingSpinnerOverlay
        isLoading={!ready}
        hideContentWhileLoading={true}
        wrap={true}
        backgroundColor={theme.colors.cardBgColor}
        wrapStyle={ready ? undefined : { minHeight: 320 }}
        spinnerSize="large"
      >
        {name && <PublicBotCardMainInfo name={name} />}
        <Text style={{ marginTop: 12 }}>{t("guided.virtual_only")}</Text>
        <Row $first>
          <OnboardingStyles.OverviewTitle style={{ margin: 0 }}>
            {t("bot_overview.symbol")}
          </OnboardingStyles.OverviewTitle>
          <TextWithIcon
            text={symbol}
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
            iconSize="l"
            textStyle={{ color: theme.colors.primaryText }}
          />
        </Row>
        {gridFacts.map((fact) => (
          <Row key={fact.label}>
            <OnboardingStyles.OverviewTitle style={{ margin: 0 }}>
              {fact.label}
            </OnboardingStyles.OverviewTitle>
            <Text>{fact.value}</Text>
          </Row>
        ))}
        <AccountsList
          symbol={symbol}
          isBot={true}
          isOnboarding={true}
          preferredExchange={account?.exchange ?? null}
        />
        {onShowConfiguration && (
          <Button
            type="link"
            style={{ paddingLeft: 0, marginTop: 12 }}
            onClick={onShowConfiguration}
            data-testid={ONBOARDING_BOT_CONFIGURATION_ID}
          >
            {/* It opens the order summary, not the full configuration. */}
            {t("intent.confirm_orders")}
          </Button>
        )}
        <Amount>
          <AmountSelector />
        </Amount>
        <Gap />
      </LoadingSpinnerOverlay>
      <PhoneActionBar busy={!ready}>
        {createButton}
        {onCustomize && (
          <LinkButton
            type="link"
            onClick={onCustomize}
            data-testid={ONBOARDING_CUSTOMIZE_ID}
          >
            {t("intent.confirm_customize")}
          </LinkButton>
        )}
      </PhoneActionBar>
    </OnboardingStyles.OverviewCard>
  );
};

export default OnboardingConfirmPanel;

const Row = styled(Flex)<{ $first?: boolean }>`
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding-bottom: 12px;
  margin: ${(p) => (p.$first ? "20px 0 12px" : "0 0 12px")};
`;
const Amount = styled.div`
  margin-top: 16px;
`;
const Gap = styled.div`
  height: 16px;
  @media (max-width: 560px) {
    display: none;
  }
`;
